// src/pages/ComingSoon.jsx
import React, { useEffect, useRef } from "react";

export default function ComingSoon() {
  const particlesRef = useRef(null);

  useEffect(() => {
    const styleTag = document.createElement("style");
    styleTag.innerHTML = `
      @keyframes floatY {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-18px); }
      }
      @keyframes fadeIn {
        0% { opacity: 0; transform: translateY(24px); }
        100% { opacity: 1; transform: translateY(0); }
      }
      .cs-fade-1 { animation: fadeIn 0.8s ease forwards; }
      .cs-fade-2 { animation: fadeIn 0.8s 0.2s ease forwards; opacity: 0; }
      .cs-fade-3 { animation: fadeIn 0.8s 0.4s ease forwards; opacity: 0; }
      .cs-float { animation: floatY 7s ease-in-out infinite; }
    `;
    document.head.appendChild(styleTag);

    // Particle system
    function createParticle() {
      const p = document.createElement("div");
      p.style.cssText = `
        position:absolute; border-radius:50%; pointer-events:none;
        width:${Math.random() * 12 + 4}px; height:${Math.random() * 12 + 4}px;
        left:${Math.random() * 100}%; top:${Math.random() * 100}%;
        background:radial-gradient(circle, rgba(99,102,241,0.6) 0%, rgba(99,102,241,0) 70%);
        animation:floatY ${5 + Math.random() * 5}s ease-in-out infinite ${Math.random() * 4}s;
      `;
      return p;
    }

    const container = particlesRef.current;
    let iv;
    if (container) {
      for (let i = 0; i < 8; i++) container.appendChild(createParticle());
      iv = setInterval(() => {
        if (container.children.length > 12) container.removeChild(container.children[0]);
        container.appendChild(createParticle());
      }, 2500);

      const onMouse = (e) => {
        const mx = (e.clientX / window.innerWidth - 0.5) * 20;
        const my = (e.clientY / window.innerHeight - 0.5) * 20;
        document.querySelectorAll(".cs-parallax").forEach((el, i) => {
          const s = (i + 1) * 0.4;
          el.style.transform = `translate(${mx * s}px, ${my * s}px)`;
        });
      };
      document.addEventListener("mousemove", onMouse);
      return () => {
        clearInterval(iv);
        document.removeEventListener("mousemove", onMouse);
        if (document.head.contains(styleTag)) document.head.removeChild(styleTag);
      };
    }
  }, []);

  const features = [
    {
      title: "Smart Detection",
      desc: "Advanced neural networks identify persons and machinery in real-time with 99.2% accuracy.",
      color: "from-blue-500 to-cyan-400",
      icon: "🎯",
    },
    {
      title: "Precision Analytics",
      desc: "Detailed insights and predictive analytics to optimize workflows and reduce costs.",
      color: "from-violet-500 to-purple-400",
      icon: "📊",
    },
    {
      title: "24/7 Monitoring",
      desc: "Automated alerts and comprehensive progress tracking around the clock.",
      color: "from-emerald-500 to-teal-400",
      icon: "⚡",
    },
  ];

  const stats = [
    { num: "99.2%", label: "Accuracy",        grad: "from-blue-400 to-cyan-400" },
    { num: "24/7",  label: "Monitoring",       grad: "from-violet-400 to-purple-400" },
    { num: "<1s",   label: "Analysis Speed",   grad: "from-emerald-400 to-teal-400" },
    { num: "AI",    label: "Powered Core",     grad: "from-rose-400 to-pink-400" },
  ];

  return (
    <div className="min-h-screen bg-[#060610] text-white overflow-x-hidden">
      {/* Particle layer */}
      <div ref={particlesRef} className="fixed inset-0 z-0 pointer-events-none" />

      {/* Background gradient blobs */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -left-32 w-[600px] h-[600px]
          bg-gradient-to-br from-blue-600/20 to-violet-600/10 rounded-full blur-3xl cs-parallax" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px]
          bg-gradient-to-br from-violet-600/15 to-cyan-600/10 rounded-full blur-3xl cs-parallax" />
      </div>

      {/* Navbar */}
      <nav className="relative z-10 flex items-center justify-between px-6 sm:px-12 py-6">
        <div className="text-xl font-bold bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent">
          LucidX
        </div>
        <a href="/login"
          className="px-4 py-2 rounded-xl border border-white/10 text-sm font-medium text-white/80
            hover:bg-white/5 hover:text-white transition-all duration-200">
          Sign in →
        </a>
      </nav>

      {/* Hero */}
      <section className="relative z-10 flex flex-col items-center justify-center
        min-h-[80vh] text-center px-6 pt-8 pb-20">

        {/* Floating shapes */}
        <div className="absolute top-24 left-[8%] cs-parallax cs-float pointer-events-none">
          <div className="w-14 h-14 rounded-2xl border border-blue-400/20 rotate-12" />
        </div>
        <div className="absolute top-40 right-[10%] cs-parallax pointer-events-none" style={{ animationDelay: "2s" }}>
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500/20 to-cyan-400/20 cs-float" />
        </div>
        <div className="absolute bottom-20 left-[15%] cs-parallax pointer-events-none" style={{ animationDelay: "4s" }}>
          <div className="w-7 h-7 rounded-lg bg-emerald-400/20 rotate-45 cs-float" />
        </div>

        <div className="cs-fade-1">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full
            border border-white/10 bg-white/5 text-xs text-white/60 font-medium mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Coming Soon — Revolutionary AI Detection
          </div>
        </div>

        <h1 className="cs-fade-2 text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight mb-6 max-w-4xl">
          AI-Powered{" "}
          <span className="bg-gradient-to-r from-blue-400 via-violet-400 to-cyan-400 bg-clip-text text-transparent">
            Construction
          </span>{" "}
          Intelligence
        </h1>

        <p className="cs-fade-3 text-lg sm:text-xl text-white/50 max-w-2xl leading-relaxed mb-10">
          Real-time detection, precision analytics, and live monitoring that transforms
          how the construction industry operates.
        </p>

        <div className="cs-fade-3 flex flex-wrap gap-3 justify-center">
          <div className="px-5 py-3 rounded-2xl bg-white/5 border border-white/10 text-sm text-white/60">
            🚧 &nbsp;Platform launching soon
          </div>
          <a href="/login"
            className="px-5 py-3 rounded-2xl bg-blue-600 hover:bg-blue-700 text-sm font-semibold
              text-white transition-all duration-200 shadow-lg shadow-blue-600/30 active:scale-95">
            Access Dashboard →
          </a>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-5 h-8 border border-white/20 rounded-full flex justify-center pt-1.5">
            <div className="w-0.5 h-2 bg-blue-400 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="relative z-10 py-12 px-6">
        <div className="max-w-4xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s, i) => (
            <div key={i} className="rounded-2xl bg-white/5 border border-white/8 px-4 py-4 text-center
              hover:bg-white/8 transition-all duration-200">
              <div className={`text-2xl sm:text-3xl font-bold bg-gradient-to-r ${s.grad} bg-clip-text text-transparent mb-1`}>
                {s.num}
              </div>
              <div className="text-xs text-white/40">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="relative z-10 py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Powered by{" "}
              <span className="bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent">
                Advanced AI
              </span>
            </h2>
            <p className="text-white/50 max-w-xl mx-auto">
              Cutting-edge computer vision combined with real-time analysis delivers unprecedented site insights.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <div key={i} className="rounded-2xl bg-white/4 border border-white/8 p-6
                hover:bg-white/7 hover:border-white/15 hover:-translate-y-1
                transition-all duration-200 group">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${f.color} bg-opacity-20
                  flex items-center justify-center text-xl mb-4`}>
                  {f.icon}
                </div>
                <h3 className="font-bold text-white mb-2">{f.title}</h3>
                <p className="text-sm text-white/50 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5 py-10 px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-xl font-bold bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent">
            LucidX
          </div>
          <p className="text-xs text-white/30">© 2025 LucidX. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
